import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLang } from "../i18n/useLang";
import { getLegalPage } from "../i18n/legalPages";

const LOGO =
  "https://customer-assets.emergentagent.com/job_focus-love-app/artifacts/wqa1c5lx_reveal%20logo.png";

const EMAIL_SPLIT = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
const isEmail = (s) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(s);

function Linkify({ text }) {
  return (
    <>
      {text.split(EMAIL_SPLIT).map((part, i) =>
        isEmail(part) ? (
          <a key={i} href={`mailto:${part}`} className="text-reveal-rose hover:text-reveal-cream transition-colors">
            {part}
          </a>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

const Bullets = ({ items }) => (
  <ul className="space-y-3 mt-3">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-3 text-reveal-cream/75 leading-relaxed">
        <span className="mt-2 shrink-0 w-1.5 h-1.5 rounded-full bg-reveal-rose" />
        <span><Linkify text={item} /></span>
      </li>
    ))}
  </ul>
);

export default function LegalPage({ page }) {
  const lang = useLang();
  const t = getLegalPage(page, lang);

  return (
    <div className="min-h-screen bg-reveal-black text-reveal-cream font-body px-5 py-16" data-testid={`legal-${page}-page`}>
      <div className="max-w-2xl mx-auto">
        <Link
          to="/landing"
          data-testid={`legal-${page}-back-link`}
          className="inline-flex items-center gap-2 text-sm text-reveal-rose hover:text-reveal-cream transition-colors mb-10"
        >
          <ArrowLeft className="w-4 h-4" /> {t.back}
        </Link>

        <div className="flex flex-col items-center text-center mb-12">
          <img src={LOGO} alt="Reveal" className="w-24 h-24 object-contain mb-6" data-testid={`legal-${page}-logo`} />
          <h1 className="font-heading text-4xl sm:text-5xl text-reveal-cream mb-4">{t.title}</h1>
          <p className="text-reveal-cream/70 leading-relaxed max-w-xl"><Linkify text={t.intro} /></p>
        </div>

        <div className="space-y-11">
          {t.sections.map((s, i) => (
            <section key={i} data-testid={`legal-${page}-section-${i + 1}`}>
              <h2 className="font-heading text-2xl text-reveal-rose mb-3">{s.h}</h2>
              {s.p && <p className="text-reveal-cream/75 leading-relaxed"><Linkify text={s.p} /></p>}
              {s.items && <Bullets items={s.items} />}
              {s.outro && <p className="text-reveal-cream/75 leading-relaxed mt-4"><Linkify text={s.outro} /></p>}
            </section>
          ))}

          <section data-testid={`legal-${page}-contact`}>
            <h2 className="font-heading text-2xl text-reveal-rose mb-3">{t.contact.h}</h2>
            <div className="rounded-2xl border border-reveal-rose/20 bg-reveal-burgundy/40 p-5 space-y-2 text-reveal-cream/80">
              {t.contact.lines.map((line, i) => (
                <p key={i}><Linkify text={line} /></p>
              ))}
            </div>
          </section>
        </div>

        <p className="mt-12 text-sm text-reveal-cream/45" data-testid={`legal-${page}-effective`}>{t.effective}</p>

        <div className="mt-10 pt-8 border-t border-reveal-rose/10 text-center">
          <Link
            to="/landing"
            data-testid={`legal-${page}-home-link`}
            className="inline-flex items-center gap-2 text-sm text-reveal-rose hover:text-reveal-cream transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> {t.home}
          </Link>
        </div>
      </div>
    </div>
  );
}
